import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AccessTime,
  CalendarMonth,
  CheckCircle,
  EditCalendar,
  ExpandMore,
  Person,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { isMobile } from "react-device-detect";
import { CommonDialog } from "@/components/common/CommonDialog";
import { RescheduleContent } from "@/components/schedule/components/RescheduleContent";
import { useAppointmentReschedule } from "@/components/schedule/useAppointmentReschedule";
import {
  buildRescheduleEntries,
  groupRescheduleEntriesByPatient,
  type RescheduleAppointmentEntry,
} from "./batchRescheduleUtils";

interface BatchRescheduleDialogProps {
  open: boolean;
  onClose: () => void;
  appointments: fhir5.Appointment[];
  patients: fhir5.Patient[];
  onComplete?: () => void;
}

interface RescheduledSlotSummary {
  dateLabel: string;
  timeLabel: string;
}

/** Altura aproximada de um item dense com 2 linhas de texto. */
const APPOINTMENT_ITEM_HEIGHT = 58;
const MAX_VISIBLE_APPOINTMENTS = 3;
const PATIENT_APPOINTMENTS_MAX_HEIGHT =
  APPOINTMENT_ITEM_HEIGHT * MAX_VISIBLE_APPOINTMENTS;
const MODAL_BODY_HEIGHT = 580;
const SIDEBAR_WIDTH = 340;

const truncatedTextSx = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function BatchRescheduleDetailPanel({
  entry,
  isActive,
  onRescheduled,
}: {
  entry: RescheduleAppointmentEntry;
  isActive: boolean;
  onRescheduled: (summary: RescheduledSlotSummary) => void;
}) {
  const {
    loading,
    loadingSlots,
    availableSlots,
    availableDates,
    slotsByDate,
    selectedDate,
    setSelectedDate,
    selectedSlot,
    setSelectedSlot,
    currentSlot,
    handleReschedule,
  } = useAppointmentReschedule({
    appointment: entry.appointment,
    enabled: isActive,
    onSuccess: () => {
      const targetSlot = availableSlots.find((slot) => slot.id === selectedSlot);
      onRescheduled({
        dateLabel: targetSlot?.start
          ? dayjs(targetSlot.start).format("DD/MM/YYYY")
          : "—",
        timeLabel: targetSlot?.start
          ? dayjs(targetSlot.start).format("HH:mm")
          : "—",
      });
    },
  });

  if (!isActive) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ flex: 1, minHeight: 240, color: "text.secondary", p: 3 }}
      >
        <EditCalendar sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
        <Typography variant="body2" textAlign="center">
          Clique numa consulta ao lado.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, height: "100%", overflow: "hidden" }}>
      <Stack spacing={0.75} sx={{ flexShrink: 0 }}>
        <Typography variant="overline" color="text.secondary" lineHeight={1.2}>
          Escolha o horário
        </Typography>
        <Typography
          variant="h6"
          fontWeight={700}
          lineHeight={1.3}
          title={entry.patientName}
          sx={truncatedTextSx}
        >
          {entry.patientName}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0, overflow: "hidden" }}
        >
          <Chip
            size="small"
            variant="outlined"
            icon={<AccessTime sx={{ fontSize: 16 }} />}
            label={`Era: ${entry.dateLabel} às ${entry.timeLabel}`}
            sx={{
              maxWidth: "100%",
              "& .MuiChip-label": truncatedTextSx,
            }}
          />
          {entry.appointment.start && (
            <Typography variant="caption" color="text.secondary">
              {dayjs(entry.appointment.start).locale("pt-br").format("dddd")}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <RescheduleContent
          loadingSlots={loadingSlots}
          availableSlots={availableSlots}
          availableDates={availableDates}
          slotsByDate={slotsByDate}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedSlot={selectedSlot}
          setSelectedSlot={setSelectedSlot}
          onCancel={() => undefined}
          onConfirm={handleReschedule}
          loading={loading}
          currentSlot={currentSlot}
          appointmentStatus={entry.appointment.status}
          hideActions
          embedded
        />
      </Box>

      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="flex-end"
        pt={1}
        borderTop={1}
        borderColor="divider"
        sx={{ flexShrink: 0 }}
      >
        <Button
          variant="contained"
          startIcon={<EditCalendar />}
          disabled={!selectedSlot || loading}
          onClick={handleReschedule}
        >
          {loading ? "Salvando..." : "Confirmar"}
        </Button>
      </Stack>
    </Stack>
  );
}

export function BatchRescheduleDialog({
  open,
  onClose,
  appointments,
  patients,
  onComplete,
}: BatchRescheduleDialogProps) {
  const theme = useTheme();
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("md"));
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null);
  const [expandedPatients, setExpandedPatients] = useState<Record<string, boolean>>(
    {}
  );
  const [completedMap, setCompletedMap] = useState<
    Record<string, RescheduledSlotSummary>
  >({});

  const entries = useMemo(
    () => buildRescheduleEntries(appointments, patients),
    [appointments, patients]
  );
  const patientGroups = useMemo(
    () => groupRescheduleEntriesByPatient(entries),
    [entries]
  );
  const skippedCount = Math.max(0, appointments.length - entries.length);
  const completedCount = Object.keys(completedMap).length;
  const allCompleted = entries.length > 0 && completedCount === entries.length;
  const progressValue =
    entries.length > 0 ? (completedCount / entries.length) * 100 : 0;

  const activeEntry = useMemo(
    () => entries.find((entry) => entry.appointment.id === activeEntryId) ?? null,
    [entries, activeEntryId]
  );

  const resetState = useCallback(() => {
    setCompletedMap({});
    setActiveEntryId(entries[0]?.appointment.id ?? null);
    setExpandedPatients(
      patientGroups.reduce<Record<string, boolean>>((acc, group) => {
        acc[group.patientId] = true;
        return acc;
      }, {})
    );
  }, [entries, patientGroups]);

  useEffect(() => {
    if (!open) return;
    resetState();
  }, [open, appointments, resetState]);

  useEffect(() => {
    if (!activeEntryId) return;

    const owningGroup = patientGroups.find((group) =>
      group.appointments.some((entry) => entry.appointment.id === activeEntryId)
    );
    if (!owningGroup) return;

    setExpandedPatients((current) =>
      current[owningGroup.patientId]
        ? current
        : { ...current, [owningGroup.patientId]: true }
    );
  }, [activeEntryId, patientGroups]);

  const handleClose = () => {
    if (completedCount > 0) {
      onComplete?.();
    }
    onClose();
  };

  const handleRescheduled = (entryId: string, summary: RescheduledSlotSummary) => {
    setCompletedMap((current) => {
      const nextMap = { ...current, [entryId]: summary };
      const nextPending = entries.find((entry) => {
        const id = entry.appointment.id;
        if (!id || id === entryId) return false;
        return !nextMap[id];
      });
      if (nextPending?.appointment.id) {
        setActiveEntryId(nextPending.appointment.id);
      }
      return nextMap;
    });
  };

  const handleFinish = () => {
    onComplete?.();
    onClose();
  };

  return (
    <CommonDialog
      handleClose={handleClose}
      isOpen={open}
      mobileView={isMobile}
      maxWidth="lg"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ minWidth: 0 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}
            >
              <CalendarMonth color="primary" sx={{ flexShrink: 0 }} />
              <Typography variant="h6" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
                Reagendar consultas
              </Typography>
            </Stack>
            {entries.length > 0 && (
              <Chip
                size="small"
                color={allCompleted ? "success" : "primary"}
                variant={allCompleted ? "filled" : "outlined"}
                sx={{ flexShrink: 0, maxWidth: { xs: 140, sm: "none" } }}
                label={
                  allCompleted
                    ? "Tudo feito"
                    : `${completedCount} de ${entries.length} feitas`
                }
              />
            )}
          </Stack>

          {entries.length > 0 && (
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{ height: 6, borderRadius: 999 }}
            />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, px: { xs: 1.5, sm: 2.5 }, pb: 2.5 }}>
        {entries.length === 0 ? (
          <Stack spacing={2} sx={{ minWidth: { xs: "100%", sm: 360 } }}>
            <Alert severity="info">
              Nenhuma consulta confirmada na seleção. Quem ainda não pagou não
              pode ser reagendado aqui.
            </Alert>
            <Button variant="outlined" onClick={handleClose} sx={{ alignSelf: "flex-end" }}>
              Fechar
            </Button>
          </Stack>
        ) : allCompleted ? (
          <Stack spacing={2.5} alignItems="center" sx={{ py: 3 }}>
            <CheckCircle color="success" sx={{ fontSize: 56 }} />
            <Stack spacing={0.75} alignItems="center" textAlign="center">
              <Typography variant="h6" fontWeight={700}>
                Pronto!
              </Typography>
              <Typography variant="body2" color="text.secondary" maxWidth={420}>
                {entries.length === 1
                  ? "A consulta foi reagendada."
                  : "Todas as consultas foram reagendadas."}
              </Typography>
            </Stack>
            <Button variant="contained" onClick={handleFinish}>
              Concluir
            </Button>
          </Stack>
        ) : (
          <Stack
            direction={isCompactLayout ? "column" : "row"}
            sx={{
              height: { xs: "auto", md: MODAL_BODY_HEIGHT },
              maxHeight: { xs: "78vh", md: `min(${MODAL_BODY_HEIGHT}px, 80vh)` },
              minHeight: { xs: 400, md: MODAL_BODY_HEIGHT },
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", md: SIDEBAR_WIDTH },
                flexShrink: 0,
                height: { md: "100%" },
                maxHeight: { xs: "42vh", md: "100%" },
                minHeight: 0,
                bgcolor: "grey.50",
                borderRight: { md: 1 },
                borderBottom: { xs: 1, md: 0 },
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {skippedCount > 0 && (
                <Alert severity="warning" sx={{ m: 1.5, py: 0.25, flexShrink: 0 }}>
                  {skippedCount}{" "}
                  {skippedCount === 1
                    ? "1 consulta sem pagamento ficou de fora"
                    : `${skippedCount} consultas sem pagamento ficaram de fora`}
                </Alert>
              )}

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  px: 1,
                  pb: 1,
                }}
              >
                {patientGroups.map((group) => {
                  const pendingCount = group.appointments.filter(
                    (entry) =>
                      entry.appointment.id &&
                      !completedMap[entry.appointment.id]
                  ).length;
                  const completedInGroup =
                    group.appointments.length - pendingCount;

                  return (
                    <Accordion
                      key={group.patientId}
                      expanded={expandedPatients[group.patientId] ?? false}
                      onChange={(_event, expanded) => {
                        setExpandedPatients((current) => ({
                          ...current,
                          [group.patientId]: expanded,
                        }));
                      }}
                      disableGutters
                      elevation={0}
                      sx={{
                        bgcolor: "background.paper",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: "10px !important",
                        mb: 1,
                        overflow: "hidden",
                        "&:before": { display: "none" },
                        "&.Mui-expanded": { mb: 1 },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore sx={{ flexShrink: 0 }} />}
                        sx={{
                          minHeight: 52,
                          px: 1,
                          overflow: "hidden",
                          "& .MuiAccordionSummary-content": {
                            my: 0.75,
                            alignItems: "center",
                            minWidth: 0,
                            overflow: "hidden",
                            marginRight: 1,
                          },
                          "& .MuiAccordionSummary-expandIconWrapper": {
                            flexShrink: 0,
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ minWidth: 0, flex: 1, pr: 0.5 }}
                        >
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: 12,
                              bgcolor: "primary.main",
                              flexShrink: 0,
                            }}
                          >
                            {getInitials(group.patientName)}
                          </Avatar>
                          <Stack spacing={0} sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              title={group.patientName}
                              sx={truncatedTextSx}
                            >
                              {group.patientName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              title={
                                pendingCount > 0
                                  ? completedInGroup > 0
                                    ? `${completedInGroup} feita${completedInGroup === 1 ? "" : "s"}, faltam ${pendingCount}`
                                    : `Faltam ${pendingCount}`
                                  : "Feito"
                              }
                              sx={truncatedTextSx}
                            >
                              {pendingCount > 0
                                ? completedInGroup > 0
                                  ? `${completedInGroup} feita${completedInGroup === 1 ? "" : "s"} · faltam ${pendingCount}`
                                  : `Faltam ${pendingCount}`
                                : "Feito"}
                            </Typography>
                          </Stack>
                        </Stack>
                      </AccordionSummary>

                      <AccordionDetails sx={{ p: 0, pt: 0, overflow: "hidden" }}>
                        <Box
                          sx={{
                            maxHeight:
                              group.appointments.length > MAX_VISIBLE_APPOINTMENTS
                                ? PATIENT_APPOINTMENTS_MAX_HEIGHT
                                : "none",
                            overflowY:
                              group.appointments.length > MAX_VISIBLE_APPOINTMENTS
                                ? "auto"
                                : "visible",
                            overflowX: "hidden",
                            px: 0.5,
                            pb: 0.5,
                            pr:
                              group.appointments.length > MAX_VISIBLE_APPOINTMENTS
                                ? 0.5
                                : 0.5,
                          }}
                        >
                          <List dense disablePadding>
                            {group.appointments.map((entry) => {
                              const entryId = entry.appointment.id ?? "";
                              const isCompleted = Boolean(completedMap[entryId]);
                              const isActive = activeEntryId === entryId;
                              const rescheduled = completedMap[entryId];

                              return (
                                <ListItemButton
                                  key={entryId}
                                  selected={isActive}
                                  disabled={isCompleted}
                                  onClick={() => setActiveEntryId(entryId)}
                                  sx={{
                                    mx: 0.5,
                                    mb: 0.5,
                                    minHeight: APPOINTMENT_ITEM_HEIGHT,
                                    borderRadius: 1.5,
                                    border: 1,
                                    borderColor: isActive
                                      ? "primary.main"
                                      : "transparent",
                                    bgcolor: isCompleted
                                      ? "success.light"
                                      : isActive
                                        ? "grey.50"
                                        : "transparent",
                                    overflow: "hidden",
                                    ...(isCompleted && {
                                      "& .MuiListItemText-primary": {
                                        color: "success.dark",
                                      },
                                    }),
                                    "&.Mui-selected": {
                                      bgcolor: "grey.50",
                                    },
                                    "&.Mui-disabled": {
                                      opacity: 1,
                                    },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 32, flexShrink: 0 }}>
                                    {isCompleted ? (
                                      <CheckCircle color="success" fontSize="small" />
                                    ) : isActive ? (
                                      <EditCalendar color="primary" fontSize="small" />
                                    ) : (
                                      <RadioButtonUnchecked
                                        color="disabled"
                                        fontSize="small"
                                      />
                                    )}
                                  </ListItemIcon>
                                  <ListItemText
                                    sx={{ minWidth: 0, overflow: "hidden" }}
                                    primary={
                                      isCompleted && rescheduled
                                        ? `${rescheduled.dateLabel} · ${rescheduled.timeLabel}`
                                        : `${entry.dateLabel} · ${entry.timeLabel}`
                                    }
                                    secondary={
                                      isCompleted
                                        ? "Feito"
                                        : entry.appointment.start
                                          ? dayjs(entry.appointment.start)
                                              .locale("pt-br")
                                              .format("dddd")
                                          : undefined
                                    }
                                    primaryTypographyProps={{
                                      fontWeight: isActive ? 700 : 500,
                                      fontSize: "0.875rem",
                                      noWrap: true,
                                      title:
                                        isCompleted && rescheduled
                                          ? `${rescheduled.dateLabel} · ${rescheduled.timeLabel}`
                                          : `${entry.dateLabel} · ${entry.timeLabel}`,
                                    }}
                                    secondaryTypographyProps={{
                                      fontSize: "0.75rem",
                                      noWrap: true,
                                    }}
                                  />
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>

              {patientGroups.some((group) => group.appointments.length > 1) && (
                <Alert
                  severity="info"
                  icon={<Person fontSize="small" />}
                  sx={{
                    m: 1.5,
                    mt: 0,
                    py: 0,
                    flexShrink: 0,
                    overflow: "hidden",
                    "& .MuiAlert-message": {
                      fontSize: "0.75rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  Mais de uma consulta? Marque uma de cada vez.
                </Alert>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                height: { md: "100%" },
                maxHeight: { xs: "36vh", md: "100%" },
                p: { xs: 1.5, sm: 2.5 },
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
                overflow: "hidden",
              }}
            >
              {activeEntry ? (
                <BatchRescheduleDetailPanel
                  key={activeEntry.appointment.id}
                  entry={activeEntry}
                  isActive
                  onRescheduled={(summary) => {
                    if (!activeEntry.appointment.id) return;
                    handleRescheduled(activeEntry.appointment.id, summary);
                  }}
                />
              ) : (
                <BatchRescheduleDetailPanel
                  entry={entries[0]}
                  isActive={false}
                  onRescheduled={() => undefined}
                />
              )}
            </Box>
          </Stack>
        )}

        {!allCompleted && entries.length > 0 && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="text" color="inherit" onClick={handleClose}>
              {completedCount > 0 ? "Fechar" : "Cancelar"}
            </Button>
          </Stack>
        )}
      </DialogContent>
    </CommonDialog>
  );
}
